import type { ReactNode } from 'react';
import { openLink } from '@tma.js/sdk-react';
import { Check, Download, Lock, Save, Sparkles, Stethoscope, Swords, Trash2 } from 'lucide-react';

import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { crDeckImportUrl } from '@/lib/crDeckLink';

interface PillProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'brand' | 'success' | 'danger';
  className?: string;
}

/**
 * The one action pill every deck surface uses. Small enough to fit three across
 * a 340px panel, which is the whole budget the Hub gives a row of actions.
 */
export function DeckPill({
  icon,
  label,
  onClick,
  disabled,
  tone = 'default',
  className,
}: PillProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-1.5',
        'text-[11px] font-bold uppercase tracking-wide transition-opacity',
        'active:opacity-80 disabled:opacity-40',
        tone === 'brand' && 'bg-brand text-brand-fg',
        tone === 'success' && 'bg-success/15 text-success',
        tone === 'danger' && 'bg-rose-500/20 text-rose-300',
        tone === 'default' && 'bg-bg-tertiary text-brand',
        className,
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

const ICON = 'h-3.5 w-3.5';

/**
 * "Import" — hands the deck to Clash Royale via the copyDeck deep link.
 *
 * Only offered for complete decks: the game silently ignores a link with fewer
 * than 8 cards, which reads to the player as a dead button.
 *
 * Accent-toned like the other action pills (Doctor / Counter / Coach). It was
 * muted for a while on the theory that importing is a quiet utility — but it is
 * the one action that actually moves a deck into the game, so it should not
 * read as secondary.
 */
export function ImportPill({
  cards,
  label,
}: {
  /**
   * Structural, not `CRPlayerCard[]`: the advisor surfaces hand us
   * `CRCDoctorCard`s, which carry the same two fields the deep link needs.
   */
  cards: ReadonlyArray<{ id: number; evolutionLevel?: number }>;
  label: string;
}) {
  const haptics = useHaptics();
  return (
    <DeckPill
      icon={<Download className={ICON} />}
      label={label}
      disabled={cards.length < 8}
      onClick={() => {
        haptics.impact('light');
        openLink.ifAvailable(crDeckImportUrl(cards));
      }}
    />
  );
}

export function SavePill({
  saved,
  saving,
  onSave,
  label,
  savedLabel,
}: {
  saved: boolean;
  saving: boolean;
  onSave: () => void;
  label: string;
  savedLabel: string;
}) {
  return (
    <DeckPill
      icon={saved ? <Check className={ICON} /> : <Save className={ICON} />}
      label={saved ? savedLabel : label}
      disabled={saving || saved}
      tone={saved ? 'success' : 'default'}
      onClick={onSave}
    />
  );
}

/** Deck Doctor — deterministic qCrC optimize (no AI). Always available. */
export function DoctorPill({ label, onClick }: { label: string; onClick: () => void }) {
  const haptics = useHaptics();
  return (
    <DeckPill
      icon={<Stethoscope className={ICON} />}
      label={label}
      onClick={() => {
        haptics.impact('light');
        onClick();
      }}
    />
  );
}

/** Counter this deck — deterministic qCrC counter (no AI). Always available. */
export function CounterPill({ label, onClick }: { label: string; onClick: () => void }) {
  const haptics = useHaptics();
  return (
    <DeckPill
      icon={<Swords className={ICON} />}
      label={label}
      onClick={() => {
        haptics.impact('light');
        onClick();
      }}
    />
  );
}

/**
 * The AI entry point.
 *
 * Hidden entirely while `env.featureCrAiEnabled` is off (the current default) —
 * every AI-invoking CR surface is held back for now, so the pill renders
 * nothing rather than a locked affordance. When enabled, free tier still sees
 * it (padlock instead of sparkle) and the paywall lives behind the tap: a panel
 * this short can't spend its first screen selling instead of showing.
 */
export function CoachPill({
  isPro,
  label,
  onClick,
}: {
  isPro: boolean;
  label: string;
  onClick: () => void;
}) {
  const haptics = useHaptics();
  if (!env.featureCrAiEnabled) return null;
  return (
    <DeckPill
      icon={isPro ? <Sparkles className={ICON} /> : <Lock className={ICON} />}
      label={label}
      onClick={() => {
        haptics.impact('medium');
        onClick();
      }}
    />
  );
}

/** Delete with an inline confirm — no modal, the row asks and answers itself. */
export function DeletePill({
  confirming,
  deleting,
  onRequest,
  onCancel,
  onConfirm,
  labels,
}: {
  confirming: boolean;
  deleting: boolean;
  onRequest: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  labels: { delete: string; cancel: string; confirm: string; deleting: string };
}) {
  if (!confirming) {
    return (
      <DeckPill
        icon={<Trash2 className={ICON} />}
        label={labels.delete}
        onClick={onRequest}
        className="text-fg-muted"
      />
    );
  }
  return (
    <>
      <DeckPill
        icon={null}
        label={labels.cancel}
        onClick={onCancel}
        disabled={deleting}
        className="text-fg-muted"
      />
      <DeckPill
        icon={<Trash2 className={ICON} />}
        label={deleting ? labels.deleting : labels.confirm}
        onClick={onConfirm}
        disabled={deleting}
        tone="danger"
      />
    </>
  );
}
